"use client";

import { useTranslations, useLocale } from "next-intl";
import { Link, useRouter, usePathname } from "@/i18n/routing";
import { useState, useEffect, useRef } from "react";
import { createPortal } from "react-dom";
import { motion, AnimatePresence } from "framer-motion";
import { productRoutes, localeLabels, localeFull } from "@/lib/navigation";
import { Button } from "@/components/ui/Button";
import { AccentColor } from "@/lib/colors";
import ScaffcalcLogoHorizontal from "../ui/ScaffcalcLogoHorizontal";
import { ChevronDownIcon } from "@/components/ui/Icons/ChevronDownIcon";

const ease = [0.25, 0.1, 0.25, 1] as const;

// ─── Desktop Navigation ───
const navLinkBase =
  "px-4 py-2 text-sm font-body font-medium transition-colors duration-200";
const navLinkInactive = "text-text-body hover:text-text-primary";
const navLinkActive = "text-text-primary";
const navLinkClass = (active: boolean) =>
  `${navLinkBase} ${active ? navLinkActive : navLinkInactive}`;

// ─── Mobile Navigation ───
const mobileLinkBase =
  "block px-4 py-3 text-xs font-body font-semibold uppercase tracking-widest transition-colors duration-200";
const mobileLinkInactive = "text-text-body hover:text-text-primary";
const mobileLinkActive = "text-text-primary";
const mobileLinkClass = (active: boolean) =>
  `${mobileLinkBase} ${active ? mobileLinkActive : mobileLinkInactive}`;

export function Navbar() {
  const t = useTranslations("Navbar");
  const locale = useLocale();
  const router = useRouter();
  const pathname = usePathname();
  const [productOpen, setProductOpen] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const [langOpen, setLangOpen] = useState(false);
  const productBtnRef = useRef<HTMLButtonElement>(null);
  const productDropRef = useRef<HTMLDivElement>(null);
  const langBtnRef = useRef<HTMLButtonElement>(null);
  const langDropRef = useRef<HTMLDivElement>(null);
  const [dropdownPos, setDropdownPos] = useState({ top: 0, left: 0 });
  const [langPos, setLangPos] = useState({ top: 0, right: 0 });
  const [theme, setTheme] = useState<"dark" | "light">("dark");

  useEffect(() => {
    setTheme(
      document.documentElement.getAttribute("data-theme") === "light"
        ? "light"
        : "dark"
    );
  }, []);

  function toggleTheme() {
    const next = theme === "dark" ? "light" : "dark";
    setTheme(next);
    if (next === "light") {
      document.documentElement.setAttribute("data-theme", "light");
    } else {
      document.documentElement.removeAttribute("data-theme");
    }
    try {
      localStorage.setItem("scaffcalc-theme", next);
    } catch {
      /* ignore quota / privacy-mode errors */
    }
  }

  useEffect(() => {
    if (mobileOpen) {
      document.body.style.overflow = "hidden";
    } else {
      document.body.style.overflow = "";
    }
    return () => {
      document.body.style.overflow = "";
    };
  }, [mobileOpen]);

  useEffect(() => {
    const onResize = () => {
      if (window.innerWidth >= 1024) setMobileOpen(false);
    };
    window.addEventListener("resize", onResize);
    return () => window.removeEventListener("resize", onResize);
  }, []);

  useEffect(() => {
    const onClick = (e: MouseEvent) => {
      const target = e.target as Node;
      const inLang =
        langBtnRef.current?.contains(target) ||
        langDropRef.current?.contains(target);
      if (!inLang) setLangOpen(false);

      const inProduct =
        productBtnRef.current?.contains(target) ||
        productDropRef.current?.contains(target);
      if (!inProduct) setProductOpen(false);
    };
    document.addEventListener("mousedown", onClick);
    return () => document.removeEventListener("mousedown", onClick);
  }, []);

  function switchLocale(newLocale: string) {
    router.replace(pathname, { locale: newLocale as "en" | "sv" | "de" });
    setLangOpen(false);
  }

  const isActive = (href: string) =>
    href === "/" ? pathname === "/" : pathname === href;
  const isProductActive = pathname.startsWith("/product");

  return (
    <>
      {/* ━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━ */}
      {/* DESKTOP & MOBILE: Top Bar (scrolls with page) */}
      {/* ━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━ */}
      <nav
        className="relative z-50 h-[72px] flex items-center justify-center px-6 lg:px-[60px]"
        style={{
          background:
            "color-mix(in srgb, var(--color-bg-base) 55%, transparent)",
          backdropFilter: "blur(16px)",
          WebkitBackdropFilter: "blur(16px)"
        }}
      >
        {/* Hamburger (mobile only, absolute left) */}
        <button
          className="lg:hidden absolute left-6 p-2 text-text-primary hover:text-text-primary transition-colors duration-200"
          onClick={() => setMobileOpen(!mobileOpen)}
          aria-label="Toggle menu"
          aria-expanded={mobileOpen}
        >
          <svg
            className="w-6 h-6"
            fill="none"
            viewBox="0 0 24 24"
            stroke="currentColor"
          >
            {mobileOpen ? (
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M6 18L18 6M6 6l12 12"
              />
            ) : (
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M4 6h16M4 12h16M4 18h16"
              />
            )}
          </svg>
        </button>

        <div className="w-full max-w-[1728px] flex items-center justify-center lg:justify-between">
          <Link
            href="/"
            className="flex items-center shrink-0"
            style={{ color: "var(--color-text-primary)" }}
          >
            <ScaffcalcLogoHorizontal height={28} color="currentColor" />
          </Link>

          {/* Spacer for fixed pill */}
          <div className="hidden lg:block" />

          <div className="hidden lg:flex items-center gap-3">
            <button
              onClick={toggleTheme}
              className="flex items-center py-2 px-2.5 text-text-primary hover:text-text-primary/80 transition-colors duration-200"
              aria-label={
                theme === "dark"
                  ? "Switch to light mode"
                  : "Switch to dark mode"
              }
            >
              {theme === "dark" ? (
                <svg
                  className="w-[18px] h-[18px]"
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                >
                  <circle cx="12" cy="12" r="4" strokeWidth={1.5} />
                  <path
                    strokeLinecap="round"
                    strokeWidth={1.5}
                    d="M12 3v1.5M12 19.5V21M3 12h1.5M19.5 12H21M5.636 5.636l1.06 1.06M17.303 17.303l1.06 1.06M5.636 18.364l1.06-1.06M17.303 6.697l1.06-1.06"
                  />
                </svg>
              ) : (
                <svg
                  className="w-[18px] h-[18px]"
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={1.5}
                    d="M21 12.79A9 9 0 1111.21 3 7 7 0 0021 12.79z"
                  />
                </svg>
              )}
            </button>
            <div className="relative">
              <button
                ref={langBtnRef}
                onClick={() => {
                  if (!langOpen && langBtnRef.current) {
                    const rect = langBtnRef.current.getBoundingClientRect();
                    setLangPos({
                      top: rect.bottom + 8,
                      right: window.innerWidth - rect.right
                    });
                  }
                  setLangOpen(!langOpen);
                }}
                className="flex items-center gap-1.5 py-2 px-2.5 text-text-primary hover:text-text-primary/80 transition-colors duration-200"
                aria-label="Switch language"
              >
                <svg
                  className="w-[18px] h-[18px]"
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={1.5}
                    d="M21 12a9 9 0 01-9 9m9-9a9 9 0 00-9-9m9 9H3m9 9a9 9 0 01-9-9m9 9c1.657 0 3-4.03 3-9s-1.343-9-3-9m0 18c-1.657 0-3-4.03-3-9s1.343-9 3-9m-9 9a9 9 0 019-9"
                  />
                </svg>
                <ChevronDownIcon
                  className={`w-4 h-4 opacity-40 transition-transform duration-200 ${langOpen ? "rotate-180" : ""}`}
                />
              </button>
            </div>
            <Button
              href="https://app.s4.scaffcalc.com/"
              target="_blank"
              rel="noopener noreferrer"
              variant="outlined"
            >
              {t("login")}
            </Button>
            <Button href="#contact">{t("contact")}</Button>
          </div>
        </div>
      </nav>

      {/* ━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━ */}
      {/* DESKTOP ONLY: Pill Navigation Menu (fixed, centered) */}
      {/* ━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━ */}
      <div className="hidden lg:flex fixed top-0 left-1/2 -translate-x-1/2 h-[72px] items-center z-[60]">
        <motion.div
          layout
          transition={{ layout: { duration: 0.3, ease } }}
          className="flex items-center gap-2 rounded-lg px-7 py-2.5"
          style={{
            background:
              "color-mix(in srgb, var(--surface-glass-bg) 55%, transparent)",
            backdropFilter: "blur(16px)",
            WebkitBackdropFilter: "blur(16px)"
          }}
        >
          <Link href="/" className={navLinkClass(isActive("/"))}>
            {t("home")}
          </Link>

          {/* Product Dropdown */}
          <div className="relative">
            <button
              ref={productBtnRef}
              onClick={() => {
                if (!productOpen && productBtnRef.current) {
                  const rect = productBtnRef.current.getBoundingClientRect();
                  setDropdownPos({ top: rect.bottom + 8, left: rect.left });
                }
                setProductOpen(!productOpen);
              }}
              className={`${navLinkClass(isProductActive)} flex items-center gap-1.5`}
            >
              {t("product")}
              <ChevronDownIcon
                className={`w-4 h-4 opacity-40 transition-transform duration-200 ${productOpen ? "rotate-180" : ""}`}
              />
            </button>
          </div>

          {/* TODO @saudin: Customers page coming — hidden for now, don't remove */}
          {/* <Link href="/customers" className={navLinkClass}>{t('customers')}</Link> */}
          <Link href="/about" className={navLinkClass(isActive("/about"))}>
            {t("about")}
          </Link>
          <Link href="/pricing" className={navLinkClass(isActive("/pricing"))}>
            {t("pricing")}
          </Link>

          {/* Get Started — always visible in the pill */}
          <motion.div
            layout
            initial={{ width: 0, opacity: 0 }}
            animate={{ width: "auto", opacity: 1 }}
            transition={{ duration: 0.3, ease }}
            className="overflow-hidden"
          >
            <Button
              href="#contact"
              className="whitespace-nowrap"
              color={AccentColor.Success}
            >
              {t("bookDemo")}
            </Button>
          </motion.div>
        </motion.div>
      </div>

      {/* ━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━ */}
      {/* SHARED: Dropdowns (Language & Product) */}
      {/* ━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━ */}

      {/* Language Dropdown (portaled for glassmorphism) */}
      {typeof document !== "undefined" &&
        createPortal(
          <AnimatePresence>
            {langOpen && (
              <motion.div
                ref={langDropRef}
                initial={{ opacity: 0, y: -8 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -8 }}
                transition={{ duration: 0.15 }}
                className="fixed w-32 bg-bg-elevated rounded-xl p-2 z-[70]"
                style={{ top: langPos.top, right: langPos.right }}
              >
                {(["en", "sv", "de"] as const).map((loc) => (
                  <button
                    key={loc}
                    onClick={() => switchLocale(loc)}
                    className={`block w-full text-left px-4 py-2.5 text-xs font-body font-semibold uppercase tracking-widest rounded-lg transition-colors duration-200 ${
                      loc === locale
                        ? "text-text-primary tint-medium"
                        : "text-text-body hover:text-text-primary tint-hover-medium"
                    }`}
                  >
                    {localeFull[loc]}
                  </button>
                ))}
              </motion.div>
            )}
          </AnimatePresence>,
          document.body
        )}

      {/* Product Dropdown (portaled for glassmorphism) */}
      {typeof document !== "undefined" &&
        createPortal(
          <AnimatePresence>
            {productOpen && (
              <motion.div
                ref={productDropRef}
                initial={{ opacity: 0, y: -8 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -8 }}
                transition={{ duration: 0.15 }}
                className="fixed w-56 bg-bg-elevated rounded-xl p-2 z-[70]"
                style={{ top: dropdownPos.top, left: dropdownPos.left }}
              >
                {productRoutes.map((item) => (
                  <Link
                    key={item.href}
                    href={item.href}
                    className={`block px-4 py-2.5 text-xs font-body font-semibold uppercase tracking-widest ${
                      isActive(item.href)
                        ? "text-text-primary tint-medium"
                        : "text-text-body hover:text-text-primary tint-hover-medium"
                    } rounded-lg transition-colors duration-200`}
                    onClick={() => setProductOpen(false)}
                  >
                    {t(item.tKey)}
                  </Link>
                ))}
              </motion.div>
            )}
          </AnimatePresence>,
          document.body
        )}

      {/* ━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━ */}
      {/* MOBILE ONLY: Slide-out Menu */}
      {/* ━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━ */}
      <AnimatePresence>
        {mobileOpen && (
          <motion.div
            initial={{ opacity: 0, y: -12 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -12 }}
            transition={{ duration: 0.2, ease }}
            className="lg:hidden fixed top-[72px] left-0 right-0 bottom-0 z-50 px-4 py-4 space-y-1 overflow-y-auto"
            style={{ backgroundColor: "var(--color-bg-base)" }}
          >
            <Link
              href="/"
              className={mobileLinkClass(isActive("/"))}
              onClick={() => setMobileOpen(false)}
            >
              {t("home")}
            </Link>
            <div className="h-px bg-border mx-4" />
            <div className="px-4 py-2">
              <p className="text-[10px] font-body font-bold uppercase tracking-widest text-text-muted mb-2">
                {t("product")}
              </p>
              <div className="space-y-0.5 pl-2">
                {productRoutes.map((item) => (
                  <Link
                    key={item.href}
                    href={item.href}
                    className={mobileLinkClass(isActive(item.href))}
                    onClick={() => setMobileOpen(false)}
                  >
                    {t(item.tKey)}
                  </Link>
                ))}
              </div>
            </div>
            <div className="h-px bg-border mx-4" />
            {/* TODO @saudin: Customers page coming — hidden for now, don't remove */}
            {/* <Link href="/customers" className={mobileLinkClass} onClick={() => setMobileOpen(false)}>{t('customers')}</Link> */}
            <Link
              href="/about"
              className={mobileLinkClass(isActive("/about"))}
              onClick={() => setMobileOpen(false)}
            >
              {t("about")}
            </Link>
            <Link
              href="/pricing"
              className={mobileLinkClass(isActive("/pricing"))}
              onClick={() => setMobileOpen(false)}
            >
              {t("pricing")}
            </Link>
            <div className="h-px bg-border mx-4" />
            <div className="flex items-center justify-between px-4 py-3">
              <div className="flex gap-2">
                {(["en", "sv", "de"] as const).map((loc) => (
                  <button
                    key={loc}
                    onClick={() => {
                      switchLocale(loc);
                      setMobileOpen(false);
                    }}
                    className={`px-3 py-1.5 text-xs font-body font-semibold uppercase tracking-widest rounded-md transition-colors duration-200 ${
                      loc === locale
                        ? "text-text-primary tint-medium"
                        : "text-text-muted hover:text-text-primary"
                    }`}
                  >
                    {localeLabels[loc]}
                  </button>
                ))}
              </div>
              <button
                onClick={toggleTheme}
                className="flex items-center p-2 text-text-primary hover:text-text-primary/80 transition-colors duration-200"
                aria-label={
                  theme === "dark"
                    ? "Switch to light mode"
                    : "Switch to dark mode"
                }
              >
                {theme === "dark" ? (
                  <svg
                    className="w-[18px] h-[18px]"
                    fill="none"
                    viewBox="0 0 24 24"
                    stroke="currentColor"
                  >
                    <circle cx="12" cy="12" r="4" strokeWidth={1.5} />
                    <path
                      strokeLinecap="round"
                      strokeWidth={1.5}
                      d="M12 3v1.5M12 19.5V21M3 12h1.5M19.5 12H21M5.636 5.636l1.06 1.06M17.303 17.303l1.06 1.06M5.636 18.364l1.06-1.06M17.303 6.697l1.06-1.06"
                    />
                  </svg>
                ) : (
                  <svg
                    className="w-[18px] h-[18px]"
                    fill="none"
                    viewBox="0 0 24 24"
                    stroke="currentColor"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={1.5}
                      d="M21 12.79A9 9 0 1111.21 3 7 7 0 0021 12.79z"
                    />
                  </svg>
                )}
              </button>
            </div>
            <div className="h-px bg-border mx-4" />
            <div className="flex flex-wrap px-4 pt-2 gap-3">
              <Button
                href="https://app.s4.scaffcalc.com/"
                target="_blank"
                rel="noopener noreferrer"
                variant="outlined"
                fullWidth
                onClick={() => setMobileOpen(false)}
              >
                {t("login")}
              </Button>
              <Button
                href="#contact"
                fullWidth
                onClick={() => setMobileOpen(false)}
              >
                {t("contact")}
              </Button>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
}
